import React, { useState, useEffect } from "react";
import {
	createSession,
	readValues,
	setValue,
	keepSessionAlive,
} from "../functions/excelFunctions";
import {
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	TextField,
	FormHelperText,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Typography } from "@mui/material";
import { NumericFormat } from "react-number-format";
import {
	BarChart,
	YAxis,
	XAxis,
	Bar,
	CartesianGrid,
	ReferenceLine,
	LineChart,
	Line,
	Legend,
	Label,
	ResponsiveContainer,
	Tooltip,
} from "recharts";
import { SelectValues } from "../data/SelectValues.js";
import "../css/ScenarioAnalysis.css";

function ScenarioAnalysis() {
	const [sessionInfo, setSessionInfo] = useState(null);
	const [cellValues, setCellValues] = useState([]);
	const sheet = "P1-P2 Outputs";
	const functionCallDelay = 300; //ms
	const firstCell = "D2";
	const lastCell = "N25";

	const policyChoices = [
		"Extending foster allowance to SGOs and CAOs",
		"Extending Flying Start ",
		"",
	];

	const [policyScenarioNumber, setPolicyScenarioNumber] = useState();

	const [policy, setPolicy] = useState();
	const policyScenarioChoices = [
		"Only SGO",
		"Only CAO",
		"Both SGO and CAO ",
		"",
	];

	const [policyScenario, setPolicyScenario] = useState();

	const [localAuthority, setLocalAuthority] = useState();

	const [barChart1Data, setBarChart1Data] = useState([]);
	const [barChart2Data, setBarChart2Data] = useState([]);
	const [YDomainChart1, setYDomainChart1] = useState([]);
	const [YDomainChart2, setYDomainChart2] = useState([]);
	const [lineChartValues, setLineChartValues] = useState([]);

	useEffect(() => {
		createSession(setSessionInfo);
	}, []);

	useEffect(() => {
		if (cellValues.length > 0) {
			setLineChartValues(
				cellValues[1].slice(1).map((year, index) => ({
					Year: year,
					claWithout: parseInt(
						cellValues[3][index + 1].replaceAll(",", "")
					),
					claAfter: parseInt(
						cellValues[5][index + 1].replaceAll(",", "")
					),
				}))
			);
			//1ST BAR CHART
			setBarChart1Data(
				cellValues[15].slice(1).map((value, index) => ({
					Year: cellValues[1][index + 1],
					Value: value,
				}))
			);

			setYDomainChart1([
				Math.floor(
					Math.min(
						...cellValues[15].slice(1).map((val) => parseFloat(val))
					)
				),
				Math.ceil(
					Math.max(
						...cellValues[15].slice(1).map((val) => parseFloat(val))
					)
				),
			]);

			//2ND BAR CHART
			setBarChart2Data(
				cellValues[23].slice(1).map((value, index) => ({
					Year: cellValues[1][index + 1],
					Value: value,
				}))
			);

			setYDomainChart2([
				Math.floor(
					Math.min(
						...cellValues[23].slice(1).map((val) => parseFloat(val))
					)
				),
				Math.ceil(
					Math.max(
						...cellValues[23].slice(1).map((val) => parseFloat(val))
					)
				),
			]);
		}
	}, [cellValues]);

	useEffect(() => {
		if (sessionInfo) {
			let range = firstCell + ":" + lastCell;
			readValues(sessionInfo, setCellValues, sheet, range);
		}
	}, [sessionInfo]);

	const handleChange = (e, setSelection, row, column, functionName = "") => {
		e.preventDefault();
		setSelection(e.target.value);
		console.log(row, column);
		setTimeout(() => {
			if (functionName === "setPolicy") {
				if (e.target.value === "Extending Flying Start ") {
					setValue(sessionInfo, sheet, 12, 1, policyScenario);
				}
			}
		}, 1 * functionCallDelay);

		setTimeout(() => {
			if (functionName === "setPolicyScenarioNumber") {
				setValue(
					sessionInfo,
					"Underlying- P2",
					row,
					column,
					e.target.value.split(" ")[0]
				);
			} else {
				setValue(sessionInfo, sheet, row, column, e.target.value);
			}
		}, 2 * functionCallDelay);
		setTimeout(() => {
			let range = firstCell + ":" + lastCell;
			readValues(sessionInfo, setCellValues, sheet, range);
		}, 3 * functionCallDelay);
	};

	function Table({ data }) {
		// Map the data to columns and rows
		let columns = [];
		let rows = [];

		for (let i = 0; i < data[1].length; i++) {
			if (i === 0) {
				columns.push({
					field: `col${i}`,
					headerName: data[0][i],
					flex: 1,
					minWidth: 550,
					sortable: false,
				});
			} else {
				columns.push({
					field: `col${i}`,
					headerName: data[0][i],
					width: 95,
					sortable: false,
				});
			}
		}

		for (let i = 1; i < data.length; i++) {
			let row = { id: i };
			for (let j = 0; j < data[i].length; j++) {
				row[`col${j}`] = data[i][j];
			}
			rows.push(row);
		}

		return (
			<div className="table-container">
				<DataGrid
					rows={rows}
					columns={columns}
					disableRowSelectionOnClick
					disableColumnMenu
					disableColumnSelector
					disableVirtualization
					hideFooter={true}
					autoHeight={true}
					density={"compact"}
				/>
			</div>
		);
	}
	setInterval(() => {
		sessionInfo && keepSessionAlive(sessionInfo, sheet);
	}, 5 * 60 * 1000); // Send the request every 5 minutes

	return (
		<div className="page-content" id="scenarioanalysis-body">
			{sessionInfo ? (
				<div>
					<Typography variant="h4" style={{ marginBottom: 30 }}>
						Scenario Analysis
					</Typography>
					<Typography variant="body1" style={{ marginBottom: 30 }}>
						Use this dashboard to explore the impact of different
						policy scenarios on the number of CLA, the public
						finances and social costs and benefits. The results
						presented in the table are based on a Cost Benefit
						Analysis (CBA) framework. The results are visualised at
						the end of the page.
					</Typography>
					{sessionInfo && (
						<div className="multiple-selects-container">
							<div className="select-container">
								<div className="select-text">
									Select the policy
								</div>
								<FormControl
									className="selection-field"
									variant="standard"
								>
									<InputLabel id="policy-selection">
										Policy
									</InputLabel>
									<Select
										labelId="policy-selection-options"
										defaultValue={""}
										label="Select the policy"
										onChange={(event) =>
											handleChange(
												event,
												setPolicy,
												9,
												1,
												"setPolicy"
											)
										}
									>
										{policyChoices.map((option) => (
											<MenuItem
												key={option}
												value={option}
											>
												{option}
											</MenuItem>
										))}
									</Select>
									<FormHelperText>
										Choose the policy you are interested in.
										For each policy the costs and benefits
										are estimated.
									</FormHelperText>
								</FormControl>
							</div>
							<div className="select-container">
								<div className="select-text">
									Select the policy scenario
								</div>
								<FormControl
									className="selection-field"
									variant="standard"
								>
									{" "}
									{policy ===
									"Extending foster allowance to SGOs and CAOs" ? (
										<>
											<InputLabel id="policy-scenario">
												Policy scenario
											</InputLabel>

											<Select
												labelId="policy-scenario-options"
												value={policyScenario}
												label="Select the policy"
												onChange={(event) =>
													handleChange(
														event,
														setPolicyScenario,
														12,
														1
													)
												}
											>
												{policyScenarioChoices.map(
													(option) => (
														<MenuItem
															key={option}
															value={option}
														>
															{option}
														</MenuItem>
													)
												)}
											</Select>
										</>
									) : (
										<NumericFormat
											id="policy-scenario-number"
											label="Policy scenario"
											variant="standard"
											name="policy-scenario"
											value={policyScenarioNumber}
											onBlur={(event) => {
												handleChange(
													event,
													setPolicyScenarioNumber,
													26,
													3,
													"setPolicyScenarioNumber"
												);
											}}
											size="small"
											suffix=" hours of free childcare "
											thousandSeparator={true}
											inputMode="numeric"
											allowNegative={false}
											customInput={TextField}
										/>
									)}
									<FormHelperText>
										For the policy "Extending foster
										allowance to SGOs and CAOs", select the
										group to whom the policy would apply
										(SGOs, CAO, or both). For the policy
										"Extending Flying Start", type the
										number of hours of free childcare to be
										provided.
									</FormHelperText>
								</FormControl>
							</div>
							<div className="select-container">
								<div className="select-text">
									Select a Local Authority
								</div>
								<FormControl
									className="selection-field"
									variant="standard"
								>
									<InputLabel id="local-authority-selection">
										Local Authority
									</InputLabel>
									<Select
										labelId="local-authority-selection"
										defaultValue={""}
										label="Select the policy"
										onChange={(event) =>
											handleChange(
												event,
												setLocalAuthority,
												15,
												1
											)
										}
									>
										{SelectValues.ScenarioAnalysis.LocalAuthorities.map(
											(option) => (
												<MenuItem
													key={option}
													value={option}
												>
													{option}
												</MenuItem>
											)
										)}
									</Select>
									<FormHelperText>
										The impact of different policy scenarios
										can be estimated for specific local
										authorities or Wales-wide.
									</FormHelperText>
								</FormControl>
							</div>
						</div>
					)}
					{((policy ===
						"Extending foster allowance to SGOs and CAOs" &&
						typeof policyScenario !== "undefined") ||
						(policy === "Extending Flying Start " &&
							typeof policyScenarioNumber !== "undefined")) &&
					typeof localAuthority !== "undefined" ? (
						cellValues.length > 0 ? (
							<>
								<div className="outputs-data-container">
									<div className="table-container">
										<h3 className="component-title">
											{cellValues[0][0]}
										</h3>
										<Table
											data={cellValues.slice(
												1,
												cellValues.length
											)}
										/>
									</div>
								</div>
								<div id="bar-chart-container">
									<div className="chart-container">
										<h3>
											{"Impact on public finances - " +
												localAuthority}
										</h3>
										<ResponsiveContainer
											width="100%"
											height={350}
										>
											<BarChart data={barChart1Data}>
												<Bar
													dataKey="Value"
													fill="#007b67"
													barSize={30}
												/>
												<Tooltip
													cursor={{
														fill: "transparent",
													}}
												/>
												<XAxis dataKey="Year" />
												<CartesianGrid
													stroke="#ccc"
													horizontal={false}
													vertical={false}
												/>
												<YAxis
													axisLine={false}
													tick={{ dx: -5 }}
													domain={[
														YDomainChart1[0] < 0
															? YDomainChart1[0]
															: 0,
														YDomainChart1[1] > 0
															? YDomainChart1[1]
															: 0,
													]}
												>
													<Label
														value="Million Pounds"
														angle={-90}
														position="insideBottom"
														dy={-40}
														dx={-23}
													/>
												</YAxis>
												<ReferenceLine
													y={0}
													stroke="black"
												/>
											</BarChart>
										</ResponsiveContainer>
									</div>
									<div className="chart-container">
										<h3>
											{"Social CBA - " + localAuthority}
										</h3>
										<ResponsiveContainer
											width="100%"
											height={350}
										>
											<BarChart data={barChart2Data}>
												<Bar
													dataKey="Value"
													fill="#007b67"
													barSize={30}
												/>
												<Tooltip
													cursor={{
														fill: "transparent",
													}}
												/>
												<XAxis dataKey="Year" />
												<CartesianGrid
													stroke="#ccc"
													horizontal={false}
													vertical={false}
												/>
												<YAxis
													axisLine={false}
													tick={{ dx: -5 }}
													domain={[
														YDomainChart2[0] < 0
															? YDomainChart2[0]
															: 0,
														"auto",
													]}
												>
													<Label
														value="Million Pounds"
														angle={-90}
														position="insideBottom"
														dy={-40}
														dx={-23}
													/>
												</YAxis>
												<ReferenceLine
													y={0}
													stroke="black"
												/>
											</BarChart>
										</ResponsiveContainer>
									</div>
								</div>
								<div id="line-chart-container">
									<div className="chart-container">
										<h3>
											{"CLA projections - " +
												localAuthority}
										</h3>
										<ResponsiveContainer
											width="100%"
											height={350}
										>
											<LineChart data={lineChartValues}>
												<Tooltip />
												<Line
													type="linear"
													dataKey="claWithout"
													stroke="#014135"
													fill="#014135"
													name="CLA without implementing policy"
												/>
												<Line
													type="linear"
													dataKey="claAfter"
													stroke="#6FE4B3"
													fill="#6FE4B3"
													name="CLA after implementing policy"
												/>
												<CartesianGrid
													stroke="#ccc"
													horizontal={false}
													vertical={false}
												/>
												<XAxis
													dataKey="Year"
													tick={{ dy: 10 }}
													padding={{
														left: 20,
														right: 20,
													}}
												/>
												<YAxis
													axisLine={false}
													tick={{ dx: -10 }}
													domain={["auto", "auto"]}
												>
													<Label
														value="Number of CLA"
														position="insideBottom"
														angle={-90}
														dx={-40}
														dy={-40}
													/>
												</YAxis>
												<Legend />
											</LineChart>
										</ResponsiveContainer>
									</div>
								</div>
							</>
						) : (
							<div className="loader" />
						)
					) : (
						<div className="no-data-text">
							Start entering data to get the project outputs
						</div>
					)}
				</div>
			) : (
				<div className="loader" />
			)}
		</div>
	);
}

export default ScenarioAnalysis;
